import { ExtrasWithCustomUi, ProtectionExtraIds } from '../../components/ExtrasHelper';
import { SmartSendItemApiModel, Validation } from '~/lib/api/smartsend/types';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { ApplierOption } from './ApplierOption';
import { IsPackaged } from './IsPackaged';
import { ProtectionIsEnabledForService } from './ProtectionIsEnabled';
import { IsReturnCollection, IsSmartSend, CanGenerateLabel } from './SmartSendSummaryGenerator';
import { MarketplaceSupportsMarkingAsDispatched } from './MarketplaceSupportsMarkingAsDispatched';
import { Feature } from '../types/Feature';
import { ExtraAlwaysIncluded } from './ExtraAlwaysIncluded';

export default function GenerateOptions(item: SmartSendItemApiModel, validation?: Validation): ApplierOption[] {
  if (!IsSmartSend(item)) {
    if (CanGenerateLabel(item)) {
      return ['Download:CSV:Orders', 'Download:Labels'];
    } else {
      return ['Download:CSV:Orders'];
    }
  }

  if (item.Status === 'Deleted') {
    return ['Status:Active', 'Tags', 'Download:CSV:Sold'];
  }

  if (item.Status === 'Inactive') {
    return ['Status:Active', 'Status:Archived', 'Tags', 'Download:CSV:Sold'];
  }

  let output = [] as ApplierOption[];
  const p: ApplierOption = IsPackaged(item) ? 'MarkAsNotPackaged' : 'MarkAsPackaged';

  if (item.MarketplaceStatus === 'AwaitingDispatch') {
    if (CanGenerateLabel(item)) {
      output.push('Download:Labels');
    }
    output = output.concat('Download:PickingList', 'Status:Archived', 'Tags', 'Download:CSV:Booked', p);
    if (MarketplaceSupportsMarkingAsDispatched(item)) {
      output.push('Dispatch');
      output.push('Dispatch:Confirm');
    }
    return output;
  }

  if (item.MarketplaceStatus === 'Dispatched') {
    output = output.concat('Status:Archived', 'Download:PickingList', 'Tags', 'Download:CSV:Booked', p);
    if (!IsReturnCollection(item)) {
      output.push('Return');
    }
    if (CanGenerateLabel(item)) {
      output.push('Download:Labels');
    }
    return output;
  }

  if (item.MarketplaceStatus === 'Sold') {
    output = output.concat(['Status:Inactive', 'Status:Deleted', 'Tags', 'Download:CSV:Sold']);

    if (!item.Service?.Id) {
      output.push('Service:Add');
    } else {
      output.push('Service:Change');
    }
    output.push('ServiceOption');

    if (
      !item.PackageDimensions?.Weight ||
      !item.PackageDimensions?.Length ||
      !item.PackageDimensions?.Width ||
      !item.PackageDimensions?.Height
    ) {
      output.push('Dimensions:Add');
    } else {
      output.push('Dimensions:Change');
    }

    if (validation?.QuoteState?.ProductValidation?.some((x) => x.name === 'TariffCode' && x.required)) {
      if (item.MarketplaceOrderLines.some((line) => !line.Product.TariffCode)) {
        output.push('TariffCodes:Add');
      }
    }

    if (
      validation?.QuoteState?.Quote?.Service?.CollectionType === 'Collection' &&
      SettingsCache.settings.Features.includes('CollectionDates')
    ) {
      output.push('PreferredCollectionDate');
    }

    if (
      validation?.Validated &&
      validation?.Errors?.length === 0 &&
      validation?.QuoteState?.Quote?.Options?.length > 0
    ) {
      output.push('Book');

      const availableExtras = validation?.QuoteState?.Quote?.Options[0]?.AvailableExtras.map((e) => e.Id) || [];

      if (
        ProtectionIsEnabledForService(item.Service) &&
        availableExtras.find((x) => ProtectionExtraIds.includes(x)) &&
        validation?.QuoteState?.Quote?.Options[0]?.CoverPercentage < 100
      ) {
        output.push('Protection');
      }

      if (
        SettingsCache.settings.Features.includes('Extras') &&
        availableExtras.find(
          (x) =>
            !ExtrasWithCustomUi.includes(x) &&
            SettingsCache.settings.Features.includes(`Extra:${x}` as Feature) &&
            !ExtraAlwaysIncluded(x, validation?.QuoteState?.Quote?.Service)
        )
      ) {
        output.push('Extras');
      }
    }

    if (!IsReturnCollection(item)) {
      if (!item.Recipient.Email || !item.Recipient.Phone) {
        output.push('RecipientContactDetails:Add');
      } else {
        output.push('RecipientContactDetails:Change');
      }
    }
  }

  return output;
}
